<template>
  <div
    class="relative w-full"
    :class="{ 'cursor-not-allowed opacity-80': disabled }"
  >
    <label
      :for="id"
      type="text"
      class="absolute -top-2 left-2 bg-white px-2 text-sm"
    >
      {{ label }}
    </label>
    <input
      :id="id"
      v-model="query"
      class="w-full rounded border border-gray-500 px-4 py-3 outline-none focus:outline-none focus:ring-0"
      :class="[
        !showSuggestions ? 'cursor-pointer' : 'cursor-text',
        { '!cursor-default': disabled },
      ]"
      type="text"
      :disabled="disabled"
      autocomplete="off"
      @input="onInput"
      @focus="openSuggestion"
      @blur="closeSuggestion"
      @click="openSuggestion"
    />
    <ul
      v-if="showSuggestions && filteredItems.length"
      class="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded border border-gray-300 bg-white shadow-md"
      @mousedown.prevent
    >
      <li
        v-for="(item, i) in filteredItems"
        :key="item[labelKey] + i"
        class="flex cursor-pointer items-center gap-x-2 px-4 py-1.5 hover:bg-gray-100"
        @click="selectItem(item)"
      >
        <component :is="item.icon" v-if="prependIcon" class="w-5" />
        {{ item[labelKey] }}
        <component :is="item.icon" v-if="appendIcon" class="w-5" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    label: string
    id: string
    placeholder?: string
    disabled?: boolean
    data?: { [key: string]: any }[] // generic input data
    labelKey?: string // defaults to 'name'
    valueKey?: string // defaults to 'id'
    prependIcon?: boolean
    appendIcon?: boolean
  }>(),
  {
    labelKey: 'name',
    valueKey: 'id',
    disabled: false,
    prependIcon: false,
    appendIcon: false,
    data: () => [],
  },
)

const model = defineModel<{ [key: string]: any }>()
const showSuggestions = ref(false)
const query = ref('')

const filteredItems = computed(() => {
  return props.data.filter((item) =>
    String(item[props.labelKey])
      .toLowerCase()
      .includes(query.value.toLowerCase()),
  )
})

const onInput = () => {
  showSuggestions.value = true
}

const selectItem = (item: any) => {
  model.value = item
  query.value = item[props.labelKey]
  showSuggestions.value = false
}

const openSuggestion = () => {
  showSuggestions.value = true
  query.value = ''
}
const closeSuggestion = () => {
  showSuggestions.value = false
  query.value = model.value?.[props.labelKey] ?? ''
}

watch(
  () => model.value,
  (val: any) => {
    query.value = val?.[props.labelKey] ?? ''
  },
  { immediate: true },
)
</script>

<style scoped></style>
